// src/pages/admin/EmailVerificationPage.tsx
import React, { useState } from 'react';
import { useUI } from '../../context/UIContext';
import { apiClient } from '../../api/apiClient';
import '../../styles/EmailVerificationManagement.css';

interface SearchResult {
  id: string;
  type: 'user' | 'affiliate';
  userType: 'client' | 'stylist' | 'affiliate';
  name: string;
  email: string;
  emailVerified: boolean;
  verificationStatus: string;
  status?: string;
  createdAt: string;
}

const EmailVerificationPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const [searchQuery, setSearchQuery] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState<string>('all');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isVerifying, setIsVerifying] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    userId: string | null;
    userType: 'user' | 'affiliate' | null;
    userName: string | null;
  }>({
    isOpen: false,
    userId: null,
    userType: null,
    userName: null
  });

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      showSnackbar('Please enter at least 2 characters to search', 'error');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const response = await apiClient.get('/v1/admin/email-verification/search', {
        params: {
          query: searchQuery.trim(),
          ...(userTypeFilter !== 'all' && { userType: userTypeFilter }),
        },
      });
      const data = response.data;

      if (data.success) {
        setResults(data.data?.results || []);
        if (!data.data?.results || data.data.results.length === 0) {
          showSnackbar('No users found matching your search', 'info');
        }
      }
    } catch (error: any) {
      console.error('Search error:', error);

      if (error?.status === 401 || error?.status === 403) {
        showSnackbar('You are not authorized to perform this action. Please log in again.', 'error');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else if (error?.status === 400) {
        showSnackbar(error?.message || 'Invalid search query. Please check your input.', 'error');
      } else if (error?.status === 0) {
        showSnackbar('Network error. Please check your connection and try again.', 'error');
      } else {
        showSnackbar(error?.message || 'Search failed. Please try again.', 'error');
      }
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const openConfirmationModal = (userId: string, userType: 'user' | 'affiliate', name: string) => {
    setConfirmationModal({
      isOpen: true,
      userId,
      userType,
      userName: name
    });
  };

  const closeConfirmationModal = () => {
    if (!isVerifying) {
      setConfirmationModal({
        isOpen: false,
        userId: null,
        userType: null,
        userName: null
      });
    }
  };

  const handleVerifyEmail = async () => {
    if (!confirmationModal.userId || !confirmationModal.userType || !confirmationModal.userName) {
      return;
    }

    const { userId, userType, userName } = confirmationModal;
    setIsVerifying(userId);

    try {
      const response = await apiClient.post('/v1/admin/email-verification/verify', {
        userId,
        userType,
      });
      const data = response.data;

      if (data.success) {
        if (data.alreadyVerified) {
          showSnackbar(`Email for ${userName} is already verified`, 'info');
        } else {
          showSnackbar(`Email verified successfully for ${userName}`, 'success');
        }
        // Refresh search results
        handleSearch();
        // Close confirmation modal
        closeConfirmationModal();
      }
    } catch (error: any) {
      console.error('Verification error:', error);

      if (error?.status === 401 || error?.status === 403) {
        showSnackbar('You are not authorized to verify emails. Please log in again.', 'error');
        closeConfirmationModal();
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else if (error?.status === 404) {
        showSnackbar(error?.message || 'User not found. They may have been deleted.', 'error');
        handleSearch();
      } else if (error?.status === 400) {
        showSnackbar(error?.message || 'Invalid request. Please check the user ID and try again.', 'error');
      } else if (error?.status === 0) {
        showSnackbar('Network error. Please check your connection and try again.', 'error');
      } else {
        showSnackbar(error?.message || 'Verification failed. Please try again.', 'error');
      }
    } finally {
      setIsVerifying(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getUserTypeBadge = (userType: string) => {
    const badges: { [key: string]: { class: string; label: string } } = {
      client: { class: 'badge-client', label: 'Client' },
      stylist: { class: 'badge-stylist', label: 'Stylist' },
      affiliate: { class: 'badge-affiliate', label: 'Referral Agent' }
    };
    return badges[userType] || { class: 'badge-default', label: userType };
  };

  return (
    <div className="email-verification-management">
      <div className="email-verification-management__header">
        <h1>Email Verification</h1>
        <p>Search and verify user emails manually</p>
      </div>

      <div className="email-verification-management__search">
        <div className="search-controls">
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Search by name or email (minimum 2 characters)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="search-input"
            />
            <button
              onClick={handleSearch}
              disabled={isSearching || searchQuery.trim().length < 2}
              className="btn-search"
            >
              {isSearching ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Searching...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i> Search
                </>
              )}
            </button>
          </div>
          <div className="filter-group">
            <label>Filter by Type:</label>
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="client">Clients</option>
              <option value="stylist">Stylists</option>
              <option value="affiliate">Referral Agents</option>
            </select>
          </div>
        </div>
      </div>

      {hasSearched && (
        <div className="email-verification-management__results">
          {results.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-search"></i>
              <p>No users found matching your search criteria</p>
              <small>Try a different search term or adjust your filters</small>
            </div>
          ) : (
            <>
              <div className="results-header">
                <h3>Search Results ({results.length})</h3>
              </div>
              <div className="results-table-container">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Verification</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result) => {
                      const badge = getUserTypeBadge(result.userType);
                      return (
                        <tr key={`${result.type}-${result.id}`}>
                          <td>
                            <strong>{result.name}</strong>
                          </td>
                          <td>{result.email}</td>
                          <td>
                            <span className={`badge ${badge.class}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td>
                            {result.status && (
                              <span className={`badge badge-${result.status}`}>
                                {result.status}
                              </span>
                            )}
                          </td>
                          <td>
                            {result.emailVerified ? (
                              <span className="verification-badge verified">
                                <i className="fas fa-check-circle"></i> Verified
                              </span>
                            ) : (
                              <span className="verification-badge unverified">
                                <i className="fas fa-times-circle"></i> Unverified
                              </span>
                            )}
                          </td>
                          <td>
                            {result.emailVerified ? (
                              <span className="action-disabled">Already Verified</span>
                            ) : (
                              <button
                                onClick={() => openConfirmationModal(result.id, result.type, result.name)}
                                disabled={isVerifying === result.id}
                                className="btn-verify"
                              >
                                <i className="fas fa-check"></i> Verify Email
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {!hasSearched && (
        <div className="email-verification-management__instructions">
          <div className="instructions-card">
            <h3>How to Use</h3>
            <ol>
              <li>Enter a name or email address in the search box (minimum 2 characters)</li>
              <li>Optionally filter by user type (Client, Stylist, or Referral Agent)</li>
              <li>Click "Search" or press Enter</li>
              <li>Review the search results</li>
              <li>Click "Verify Email" for any unverified user</li>
            </ol>
            <div className="info-box">
              <i className="fas fa-info-circle"></i>
              <p>
                <strong>Note:</strong> This will verify the user's email and allow them to log in immediately.
                A welcome email will be sent automatically.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmationModal.isOpen && (
        <div className="modal-overlay" onClick={closeConfirmationModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verify Email</h2>
              <button
                onClick={closeConfirmationModal}
                className="modal-close"
                disabled={isVerifying !== null}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to verify the email for <strong>{confirmationModal.userName}</strong>?
              </p>
              <p className="action-note">
                This will verify the user's email and allow them to log in immediately. A welcome email will be sent automatically.
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={closeConfirmationModal}
                className="btn-secondary"
                disabled={isVerifying !== null}
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyEmail}
                className="btn-primary"
                disabled={isVerifying !== null}
              >
                {isVerifying ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Verifying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i> Verify Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailVerificationPage;
